import captureError from '@disco/utils/captureError'
import upperFirst from 'lodash/upperFirst'
import * as React from 'react'
import { connect } from 'react-redux'
import type { EmailCampaign } from 'types/campaign'
import {
  cancelScheduledCampaign,
  scheduleSendEmailCampaign,
} from '@disco/state-management/actions/campaign-details-actions'
import {
  campaignListBeginCreating,
  campaignListDelete,
  campaignListDuplicate,
} from '@disco/state-management/actions/campaign-list-actions'
import mixpanel from '@disco/api/mixpanel'
import type { CampaignStatus } from '@disco/constants/CampaignTypes'
import {
  getCampaignListSortedFiltered,
  getCampaignSearchQuery,
} from '@disco/state-management/selectors/campaigns'
import {
  formatScheduledCampaignData,
  formatTimezoneOffset,
  getCampaignsTableData,
} from '@disco/utils/emailCampaigns'
import { EmailSchedulerModal } from 'components/modals/email-scheduler'
import Button from 'components/ui/button'
import DataTable from 'components/ui/data-table'
import Heading from 'components/ui/heading'
import ModalDialog from 'components/ui/modal-dialog'
import Stack from 'components/ui/stack'
import Text from 'components/ui/text'
import { getCampaignColumns } from './getCampaignColumns'

const mapStateToProps = (state: any, props: any) => {
  const { emailCampaigns } = getCampaignListSortedFiltered(state, props.status)
  const { emailsCount, tableData } = getCampaignsTableData({
    emailCampaigns,
    limit: props.limit,
    status: props.status,
  })
  return {
    tableData,
    emailsCount,
    campaignSearchQuery: getCampaignSearchQuery(state),
  }
}

const mapDispatchToProps = {
  campaignListDuplicate,
  campaignListDelete,
  campaignListBeginCreating,
  scheduleSendEmailCampaign,
  cancelScheduledCampaign,
}

class CampaignTable extends React.Component<
  typeof mapDispatchToProps & {
    emailsCount: number
    tableData: Array<EmailCampaign>
    isFeatureEnabled: boolean
    status: CampaignStatus
    limit?: number
    campaignSearchQuery: string | null | undefined
  },
  {
    duplicateConfirmationModal:
      | {
          campaign: EmailCampaign
        }
      | null
      | undefined
    deleteConfirmationModal:
      | {
          campaign: EmailCampaign
        }
      | null
      | undefined
    rescheduleConfirmationModal:
      | {
          campaign: EmailCampaign
        }
      | null
      | undefined
    cancelScheduleConfirmationModal:
      | {
          campaign: EmailCampaign
        }
      | null
      | undefined
    scheduledData: Record<string, any> | null | undefined
  }
> {
  state = {
    duplicateConfirmationModal: null,
    deleteConfirmationModal: null,
    rescheduleConfirmationModal: null,
    cancelScheduleConfirmationModal: null,
    scheduledData: null,
  }
  _onActionClick = (
    campaign: EmailCampaign,
    action: 'Delete' | 'Duplicate' | 'Reschedule' | 'Cancel Schedule',
  ) => {
    let rescheduleData
    let timezoneOffset

    if (campaign) {
      rescheduleData = formatScheduledCampaignData(campaign)

      if (campaign.scheduled_at && campaign.scheduled_at_timezone) {
        timezoneOffset = formatTimezoneOffset(
          campaign.scheduled_at,
          campaign.scheduled_at_timezone,
        )
      }
    }

    switch (action) {
      case 'Delete':
        this.setState({
          deleteConfirmationModal: {
            campaign,
          },
        })
        break

      case 'Duplicate':
        this.setState({
          duplicateConfirmationModal: {
            campaign,
          },
        })
        break

      case 'Reschedule':
        this.setState({
          rescheduleConfirmationModal: {
            campaign,
          },
          scheduledData: rescheduleData,
        })
        break

      case 'Cancel Schedule':
        this.setState({
          cancelScheduleConfirmationModal: {
            campaign,
          },
          scheduledData: {
            rescheduleData: rescheduleData,
            timezoneOffset: timezoneOffset,
          },
        })
        break

      default:
        throw new Error('Not implemented')
    }
  }
  _handleConfirmDuplicate = () => {
    const { duplicateConfirmationModal } = this.state
    if (!duplicateConfirmationModal) return
    this.props.campaignListDuplicate(duplicateConfirmationModal.campaign.id)
    mixpanel.sendPureEvent('Email Creator - Duplicate existing email', {
      Context: 'Overview',
    })

    this._handleCancel()
  }
  _handleConfirmDelete = () => {
    const { deleteConfirmationModal } = this.state
    if (!deleteConfirmationModal) return
    this.props.campaignListDelete(deleteConfirmationModal.campaign.id)

    this._handleCancel()
  }

  _handleCancel = () => {
    this.setState({
      deleteConfirmationModal: null,
      duplicateConfirmationModal: null,
      rescheduleConfirmationModal: null,
      cancelScheduleConfirmationModal: null,
    })
  }

  _handleClose = () => {
    const { rescheduleConfirmationModal } = this.state

    if (rescheduleConfirmationModal) {
      mixpanel.sendPureEvent('Email Creator - Reschedule email', {
        Context: 'Overview',
      })
    }

    this._handleCancel()
  }

  _handleConfirmReschedule = (data: any) => {
    const { rescheduleConfirmationModal } = this.state
    if (!rescheduleConfirmationModal) return

    try {
      this.props.scheduleSendEmailCampaign(
        Number(rescheduleConfirmationModal.campaign.id),
        data,
        true,
      )
    } catch (error) {
      captureError(error)
    }
  }
  _handleCancelReschedule = () => {
    // API call returns Error: Cannot save: newer campaign exists ??
    const { cancelScheduleConfirmationModal } = this.state
    if (!cancelScheduleConfirmationModal) return

    try {
      this.props.cancelScheduledCampaign(
        Number(cancelScheduleConfirmationModal.campaign.id),
      )

      this._handleCancel()

      mixpanel.sendPureEvent('Email Creator - Cancel email scheduling', {
        Context: 'Overview',
      })
    } catch (error) {
      captureError(error)
    }
  }

  render() {
    const {
      emailsCount,
      tableData,
      status,
      limit,
      isFeatureEnabled,
      campaignSearchQuery,
    } = this.props
    const {
      deleteConfirmationModal,
      duplicateConfirmationModal,
      rescheduleConfirmationModal,
      cancelScheduleConfirmationModal,
      scheduledData,
    } = this.state
    let table = (
      <React.Fragment>
        {deleteConfirmationModal && (
          <ModalDialog
            title="Delete email"
            primaryAction={{
              caption: 'Delete',
              onClick: this._handleConfirmDelete,
            }}
            status="danger"
            onCancel={this._handleCancel}
          >
            <span>
              Are you sure you want to delete “
              <span>
                {deleteConfirmationModal.campaign.name ||
                  `Untitled ${deleteConfirmationModal.campaign.type}`}
              </span>
              <span>”? This can’t be undone.</span>
            </span>
          </ModalDialog>
        )}
        {duplicateConfirmationModal && (
          <ModalDialog
            title="Duplicate email"
            primaryAction={{
              caption: 'Duplicate',
              onClick: this._handleConfirmDuplicate,
            }}
            onCancel={this._handleCancel}
          >
            <span>
              Are you sure you want to duplicate “
              <span>
                {duplicateConfirmationModal.campaign.name ||
                  `Untitled ${duplicateConfirmationModal.campaign.type}`}
              </span>
              <span>”?</span>
            </span>
          </ModalDialog>
        )}
        {rescheduleConfirmationModal && (
          <EmailSchedulerModal
            setScheduleData={this._handleConfirmReschedule}
            onClose={this._handleClose}
            storedCampaignSchedule={scheduledData}
            caption="Schedule email"
            description="Pick a date, time and timezone for your email to go out."
            primaryButtonCaption="Schedule"
          />
        )}
        {cancelScheduleConfirmationModal && (
          <ModalDialog
            title="Cancel scheduled email"
            primaryAction={{
              caption: 'Cancel Scheduled Email',
              onClick: this._handleCancelReschedule,
            }}
            onCancel={this._handleCancel}
          >
            <span>
              Are you sure you want to cancel the scheduled time of{' '}
              {scheduledData?.rescheduleData.date?.label} at{' '}
              {scheduledData?.rescheduleData.time?.label} (GMT
              {scheduledData?.timezoneOffset}) for “
              <span>
                {cancelScheduleConfirmationModal.campaign.name ||
                  `Untitled ${cancelScheduleConfirmationModal.campaign.type}`}
              </span>
              <span>”?</span>
            </span>
          </ModalDialog>
        )}

        <DataTable
          appearance="plain"
          columns={getCampaignColumns({
            status,
            isFeatureEnabled,
            onActionClick: this._onActionClick,
            sortable: !Boolean(limit),
          })}
          defaultSort={
            !Boolean(limit)
              ? {
                  col: 1,
                  dir: 1,
                }
              : undefined
          } // "1/1" means created_at/desc
          data={tableData}
          emptyState={
            <Text type="H200">
              {campaignSearchQuery
                ? 'No results for this search'
                : `Nothing in ${status} right now`}
            </Text>
          }
          emptyStateCompact
        />
      </React.Fragment>
    )

    if (status && limit) {
      return (
        <Stack space="small">
          <Heading size="600">{upperFirst(status)}</Heading>
          <div>{table}</div>
          {tableData.length < emailsCount ? (
            <Button
              appearance="link"
              caption={`See all ${status}`}
              onClick={() => {
                mixpanel.sendPureEvent(
                  `Email Creator - Open all ${status} page`,
                )
              }}
              link={`/campaigns/list/${status}`}
            />
          ) : null}
        </Stack>
      )
    }

    return table
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CampaignTable) as any
